import '../App.css';
import { useEffect, useRef, useState } from 'react';
import { useAuth, withAuthenticationRequired } from 'react-oidc-context';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fontLibrary } from '../fonts';
import { useAppDispatch, useAppSelector, useInactivityTimer, useUser } from '../hooks';
import { useApiContext } from '../../api';
import { setTemplates } from '../../data/templateSlice';
import { promptAcceptEula } from '../../features/settings/elements/AcceptEula';
import { applyDefaultTheme } from '../../features/themes/themes';
import { promptCountDownBox } from '../../components/message-boxes/CountDownbox';
import { setEtlComponentTypesFromTemplates } from '../../features/etl-pipeline/etl-component-types/factory';
import { authenticationHealthCheck } from '../../api/healthcheck/auth-healthcheck';
// Additional imports on dealing with the task execution and polling
import {
  addPollingConfiguration,
  removePollingConfiguration,
  selectPollingConfigurations,
  setIsPolling,
} from '../../data/pollingSlice';
import { setTaskDefinitions } from '../../data/taskDefinitionSlice';
import { selectTaskExecution } from '../../data/taskExecutionSlice';
import { updatePolling } from '../../data/pollingService';
import { countDownDataApp, pollingConfigApp } from '../utils/app-helpers';
import LoadingScreen from '../utils/LoadingScreen';
import PlatformAppClient from '../utils/PlatformAppClient';

const EULA_VERSION = import.meta.env.VITE_EULA_VERSION;

function AppOnPremise() {
  // unpack list of fonts into FontAwesome's library
  library.add(...fontLibrary);

  const auth = useAuth();
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const { user, setUser } = useUser();
  const [isLoaded, setIsLoaded] = useState(false);
  const onLoadError = useRef(null);
  const pollingConfig = useAppSelector(selectPollingConfigurations);
  const taskExecutions = useAppSelector(selectTaskExecution);

  applyDefaultTheme();

  useEffect(() => {
    // Only do once at start-up
    // Ping identity provider. This triggers Authentik boot-up
    authenticationHealthCheck()
      .then((message) => console.log(message))
      .catch((error) => console.error(error));
  }, []);

  const handleInactivity = () => {
    promptCountDownBox(countDownDataApp)
      .then(() => {})
      .catch(() => {
        api.logout(true);
      });
  };

  useInactivityTimer(handleInactivity);

  const initializeApp = () => {
    api.user
      .fetchUser()
      .then((apiData) => {
        const userData = auth.user?.profile;
        if (userData!.email!.toLowerCase() !== apiData.email!.toLowerCase()) {
          // If a sessionId was not properly removed fram a previous user session, the user will be logged out
          api.logout(true);
          return Promise.reject('invalid user session');
        }
        setUser({
          email: userData!.email!.toLowerCase(),
          name: userData!.nickname,
          avatar: undefined, // userData!.picture, -- TODO: currently not passed with oidc userinfo api call in authentik
          isOrganizationAdmin: apiData.isOrganizationAdmin,
          canWrite: apiData.canWrite,
          acceptedEulaVersion: apiData.acceptedEulaVersion,
          organizations: apiData.organizations,
          isSystemAdmin: apiData.isSystemAdmin,
        });
        return api.template.fetchTemplates();
      })
      .then((templates) => {
        // store the ontology in the redux store
        return dispatch(setTemplates({ templates }));
      })
      .then(() => {
        // Fetch the external ETL component types
        return api.etl.fetchComponentTypeTemplates();
      })
      .then((etlComponentTypeTemplates) => {
        setEtlComponentTypesFromTemplates(etlComponentTypeTemplates);
        return api.task.fetchTaskDefinitions();
      })
      .then((taskDefinitions) => {
        setIsLoaded(true);
        dispatch(setTaskDefinitions({ taskDefinitions }));
      })
      .then(() => {
        return api.task.fecthAndStoreTaskExecutions();
      })
      .catch((error) => {
        onLoadError.current = error;
        return;
      });
  };

  // Automatically redirect if not authenticated
  useEffect(() => {
    if (
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !auth.activeNavigator // Prevents firing twice in React Strict Mode
    ) {
      sessionStorage.setItem('user_authentik_return_to', window.location.pathname + window.location.search);
      auth.signinRedirect();
    }

    if (auth.isAuthenticated && !isLoaded) {
      initializeApp();
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.activeNavigator, isLoaded]);

  // Load the EULA agreement, which is necessary to accept to load all BioVerse features.
  // If not accepted, the user may not access the application's features
  useEffect(() => {
    if (isLoaded && Object.keys(user).length > 0 && user.acceptedEulaVersion !== EULA_VERSION) {
      promptAcceptEula(api)
        .then(() => {
          setUser({
            ...user,
            acceptedEulaVersion: EULA_VERSION,
          });
        })
        .catch((error) => {
          console.error(error);
          api.logout(true);
        });
    }
  }, [user, isLoaded]);

  if (auth.error) {
    return <div>Auth Error: {auth.error.message}</div>;
  }

  // Task Execution features
  useEffect(() => {
    if (taskExecutions.taskExecutionList.length === 0) return;
    if (taskExecutions.taskExecutionList.some((task) => task.status === 'running' || task.status === 'queued')) {
      dispatch(addPollingConfiguration(pollingConfigApp));
    } else {
      dispatch(removePollingConfiguration({ pollingConfigId: pollingConfigApp.identifier }));
    }
  }, [taskExecutions]);

  useEffect(() => {
    if (pollingConfig.isPolling && pollingConfig.pollingConfigurations.length === 0) {
      dispatch(setIsPolling(false));
    } else {
      updatePolling(api, dispatch, pollingConfig);
    }
  }, [pollingConfig]);

  // If the auth library is processing the ?code=... URL, show a loading screen!
  // DO NOT render the rest of your app or trigger another redirect.
  if (auth.isLoading) {
    return <LoadingScreen />;
  }

  // Load the actual application
  if (auth.isAuthenticated && isLoaded) {
    return <PlatformAppClient {...user} />;
  }

  return <></>;
}

export default withAuthenticationRequired(AppOnPremise, {
  onBeforeSignin: () => {
    // Bookmark the path right before the react-oidc-context library sends the user away for authentication
    localStorage.setItem('user_authentik_return_to', window.location.pathname + window.location.search);
  },
});
